import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { usePlanner } from '../contexts/PlannerContext';
import { usePJRSummary } from '../hooks/usePJRSummary';
import { GlobalSearch } from './GlobalSearch';

/**
 * PJR B2B Navbar — 1:1 port of Claude's mock (pjr-navbar.html, Apr 30 2026).
 *
 * Structure: [logo] · [5 category links] · [lang icon · cart icon · Iniciar sesión · Registrarse]
 * All CSS lives in `index.css` under the `.pjr-navbar*` namespace.
 * SVG icons copied verbatim from the mock (stroke: currentColor, 14×14).
 */

const ICON_FLIGHTS = (
  <svg viewBox="0 0 24 24"><path d="M22 16.5l-9.5-5.5L15 4a2 2 0 0 0-2.8-2.8L5.5 9 2 8 1 9l4 3-2 3.5 1.5 1 3-2 3 4 1-1-.5-3.5z"/></svg>
);
const ICON_HOTELS = (
  <svg viewBox="0 0 24 24"><path d="M3 22V8l9-5 9 5v14"/><path d="M9 22V12h6v10"/></svg>
);
const ICON_EUROPAMUNDO = (
  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10"/></svg>
);
const ICON_SELECT = (
  <svg viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
);
const ICON_GRUPOS = (
  <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);
const ICON_LANG = (
  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10"/></svg>
);
const ICON_CART = (
  <svg viewBox="0 0 24 24"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
);
const ICON_PLANNER = (
  <svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M9 8h6M9 12h6M9 16h4"/></svg>
);
const ICON_USER = (
  <svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
);

const ICON_ACTIVITIES = (
  <svg viewBox="0 0 24 24"><path d="M12 2l3 7h7l-5.5 4 2 7-6.5-4.5L5.5 20l2-7L2 9h7z"/></svg>
);

const NAV_LINKS = [
  // { path: '/flights',    label: 'Vuelos',      icon: ICON_FLIGHTS,     key: 'flights' }, // Hidden — waiting for Amadeus integration
  { path: '/hotels',     label: 'Hoteles',     icon: ICON_HOTELS,      key: 'hotels' },
  { path: '/tours',      label: 'Europamundo', icon: ICON_EUROPAMUNDO, key: 'europamundo' },
  { path: '/activities', label: 'Actividades', icon: ICON_ACTIVITIES,  key: 'activities' },
  { path: '/pjr-select', label: 'PJR Select',  icon: ICON_SELECT,      key: 'pjr-select' },
  { path: '/grupos-incluido', label: 'Grupos', icon: ICON_GRUPOS,      key: 'grupos' },
];

export default function Navbar() {
  const { language, toggleLanguage } = useLanguage();
  const { user, logout, isAdmin } = useAuth();
  const pjrSummary = usePJRSummary();
  const { itemCount, civitatisCount } = useCart();
  const { itemCount: plannerCount } = usePlanner();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userMenuOpen]);

  // Close mobile menu when route changes (so picking a category collapses it).
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); setUserMenuOpen(false); navigate('/'); };
  const isActive = (p) => location.pathname === p;

  return (
    <nav className="pjr-navbar" data-testid="navbar">
      {/* Logo */}
      <Link to="/" className="pjr-navbar-logo" data-testid="nav-logo">
        <img src="/pjr-logo.png" alt="PJR Travel" draggable="false" data-testid="nav-logo-img" />
      </Link>

      {/* Center nav links — desktop only (hidden under 960px via CSS) */}
      <div className="pjr-navbar-center">
        {NAV_LINKS.map((n) => (
          <Link key={n.key} to={n.path}
                className={`pjr-nav-link${isActive(n.path) ? ' active' : ''}`}
                data-testid={`nav-${n.key}`}>
            {n.icon}
            {n.label}
          </Link>
        ))}
      </div>

      {/* Right actions */}
      <div className="pjr-navbar-right">
        {/* Language toggle hidden until EN translations ship */}

        {user && <GlobalSearch />}

        <Link to="/planner" className="pjr-btn-icon" title="Planner · Cotizaciones guardadas" data-testid="nav-planner">
          {ICON_PLANNER}
          {plannerCount > 0 && (
            <span className="pjr-cart-badge" data-testid="planner-badge">{plannerCount}</span>
          )}
        </Link>

        <Link to="/cart" className="pjr-btn-icon" title="Carrito" data-testid="nav-cart">
          {ICON_CART}
          {(itemCount + civitatisCount) > 0 && (
            <span className="pjr-cart-badge" data-testid="cart-badge">{itemCount + civitatisCount}</span>
          )}
        </Link>

        {user ? (
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setUserMenuOpen((o) => !o)}
                    className="pjr-btn-login flex items-center gap-2"
                    style={{ display: 'inline-flex' }}
                    data-testid="user-menu-trigger">
              {ICON_USER}
              <span className="hidden sm:inline">{(user.name || user.email || '').split(' ')[0]}</span>
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-[0_18px_40px_rgba(0,0,0,0.16)] border border-[#ececec] overflow-hidden z-[210]">
                <button className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#f4f7fb]" onClick={() => { navigate('/dashboard'); setUserMenuOpen(false); }} data-testid="nav-my-quotes">Dashboard</button>
                {isAdmin && (
                  <>
                    <button
                      className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#f4f7fb] flex items-center justify-between"
                      onClick={() => { window.open('/admin', '_blank', 'noopener,noreferrer'); setUserMenuOpen(false); }}
                      data-testid="nav-admin"
                    >
                      <span>Administración</span>
                      <span aria-hidden style={{ fontSize: 11, color: '#6b7a99' }}>↗</span>
                    </button>
                    <button
                      className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#f4f7fb] flex items-center justify-between"
                      onClick={() => { window.open('/admin/pjr/catalogo', '_blank', 'noopener,noreferrer'); setUserMenuOpen(false); }}
                      data-testid="nav-admin-pjr-cms"
                    >
                      <span>
                        CMS PJR · Productos
                        {pjrSummary?.total != null && (
                          <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#2467B0', letterSpacing: '.02em' }} data-testid="nav-admin-pjr-cms-count">
                            ({pjrSummary.total})
                          </span>
                        )}
                      </span>
                      <span aria-hidden style={{ fontSize: 11, color: '#6b7a99' }}>↗</span>
                    </button>
                  </>
                )}
                {(user?.agency_role === 'master') && (
                  <button className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#f4f7fb]" onClick={() => { navigate('/agency/profile'); setUserMenuOpen(false); }} data-testid="nav-agency-profile">Configuración de agencia</button>
                )}
                <div className="h-px bg-[#ececec]" />
                <button className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#f4f7fb] text-[#b42318]" onClick={handleLogout} data-testid="nav-logout">Cerrar sesión</button>
              </div>
            )}
          </div>
        ) : (
          <>
            <Link to="/login" className="pjr-btn-login" data-testid="nav-login">Iniciar sesión</Link>
            <Link to="/register" className="pjr-btn-register" data-testid="nav-register">Registrarse</Link>
          </>
        )}

        {/* Hamburger — visible under 960px (desktop center nav is hidden there). */}
        <button type="button" onClick={() => setMobileMenuOpen((o) => !o)}
                className="pjr-btn-icon pjr-btn-burger" aria-label="Menu"
                data-testid="nav-burger">
          {mobileMenuOpen
            ? <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            : <svg viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>}
        </button>
      </div>

      {/* Mobile flyout — slides down with the same dark navy theme. */}
      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="pjr-mobile-menu" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((n) => (
            <Link key={n.key} to={n.path}
                  className={`pjr-mobile-link${isActive(n.path) ? ' active' : ''}`}
                  data-testid={`nav-mobile-${n.key}`}>
              <span className="pjr-mobile-link-icon">{n.icon}</span>
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
